import { z } from 'zod';

/**
 * Version de los contratos. Todo documento persistido la declara en `schemaVersion`.
 * Semver: los cambios de patch (`0.2.x`) son aditivos y compatibles hacia atras; un
 * cambio de minor (`0.3.0`) es incompatible y requiere migracion.
 */
export const CONTRACTS_VERSION = '0.2.4';

/** Serie de versiones compatible con esta build (`0.2.x`). */
export const COMPATIBLE_SCHEMA_VERSION_PATTERN = /^0\.2\.(0|[1-9]\d*)$/;

/** Acepta cualquier documento de la serie `0.2.x`; los documentos nuevos usan `CONTRACTS_VERSION`. */
export const SchemaVersionSchema = z
  .string()
  .regex(COMPATIBLE_SCHEMA_VERSION_PATTERN, 'Version de contrato incompatible (se espera 0.2.x)');

export const isCompatibleSchemaVersion = (version: string): boolean =>
  COMPATIBLE_SCHEMA_VERSION_PATTERN.test(version);

/**
 * Identificador estable de un objeto (tabla, columna, medida...). Es independiente
 * del nombre visible para que renombrar no rompa referencias.
 */
export const IdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_.:-]+$/, 'Solo letras, digitos y los caracteres _ . : -');
export type Id = z.infer<typeof IdSchema>;

/** Clave local de un campo dentro de una consulta del Analysis State. */
export const FieldKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Debe ser un identificador valido');
export type FieldKey = z.infer<typeof FieldKeySchema>;

export const IsoDateTimeSchema = z.iso.datetime({ offset: true });

/** Orden por clave de campo de una consulta. */
export const SortSchema = z.strictObject({
  key: FieldKeySchema,
  direction: z.enum(['asc', 'desc']),
});
export type Sort = z.infer<typeof SortSchema>;

/** Valor escalar admitido en filtros, selecciones y literales. */
export const ScalarValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type ScalarValue = z.infer<typeof ScalarValueSchema>;

/**
 * Terminos que delatan un secreto. Ninguna clave libre (`metadata`, `options`) puede
 * contenerlos: los secretos se referencian siempre mediante `secretRefs`.
 */
export const SENSITIVE_KEY_TERMS = [
  'password',
  'passwd',
  'secret',
  'token',
  'apikey',
  'api_key',
  'api-key',
  'credential',
  'connectionstring',
  'connection_string',
  'connection-string',
  'privatekey',
  'private_key',
  'private-key',
  'accesskey',
  'access_key',
  'access-key',
] as const;

/** Sin banderas de regex para que el patron sea identico en JSON Schema (ECMA-262). */
const caseInsensitive = (term: string): string =>
  term.replace(/[a-z]/g, (char) => `[${char}${char.toUpperCase()}]`);

const SAFE_KEY_PATTERN = new RegExp(
  `^(?![\\s\\S]*(?:${SENSITIVE_KEY_TERMS.map(caseInsensitive).join('|')}))[\\s\\S]{1,128}$`,
);

/** Clave libre que no puede nombrar un secreto (`password`, `apiKey`, `token`...). */
export const SafeKeySchema = z
  .string()
  .regex(SAFE_KEY_PATTERN, 'La clave parece un secreto; use secretRefs');

export const isSensitiveKey = (key: string): boolean => !SAFE_KEY_PATTERN.test(key);

/** Metadatos libres serializables como JSON (extensiones, anotaciones de plugins). */
export const MetadataSchema = z.record(SafeKeySchema, z.json());
export type Metadata = z.infer<typeof MetadataSchema>;

/** Metadatos de ciclo de vida comunes a los documentos persistibles. */
export const DocumentMetadataSchema = z.strictObject({
  createdAt: IsoDateTimeSchema.optional(),
  updatedAt: IsoDateTimeSchema.optional(),
  createdBy: z.string().min(1).max(200).optional(),
  /** Revision monotona: soporta concurrencia optimista y undo/redo por versiones. */
  revision: z.number().int().nonnegative().default(0),
  tags: z.array(z.string().min(1).max(64)).default([]),
  /** Anotaciones de plugins o integraciones; nunca secretos. */
  extensions: MetadataSchema.optional(),
});
export type DocumentMetadata = z.infer<typeof DocumentMetadataSchema>;

/** Prefijo que distingue las extensiones aportadas por plugins (`plugin:<id>`). */
export const PluginExtensionIdSchema = z.templateLiteral([
  'plugin:',
  z.string().regex(/^[a-z0-9][a-z0-9.-]*$/),
]);
export type PluginExtensionId = z.infer<typeof PluginExtensionIdSchema>;

/** Tipos logicos de datos, independientes del tipo nativo de cada motor. */
export const LOGICAL_DATA_TYPES = [
  'string',
  'integer',
  'decimal',
  'boolean',
  'date',
  'datetime',
  'time',
  'duration',
  'binary',
  'json',
  'geography',
  'unknown',
] as const;
export const LogicalDataTypeSchema = z.enum(LOGICAL_DATA_TYPES);
export type LogicalDataType = z.infer<typeof LogicalDataTypeSchema>;

/** Tipos semanticos que enriquecen al tipo logico (fechas, geografia, identificadores...). */
export const SEMANTIC_TYPES = [
  'date',
  'year',
  'quarter',
  'month',
  'week',
  'day',
  'geo.country',
  'geo.region',
  'geo.state',
  'geo.city',
  'geo.postal-code',
  'geo.latitude',
  'geo.longitude',
  'geo.point',
  'identifier',
  'currency',
  'quantity',
  'percentage',
  'email',
  'url',
] as const;
export const SemanticTypeSchema = z.enum(SEMANTIC_TYPES);
export type SemanticType = z.infer<typeof SemanticTypeSchema>;

/** Granularidades temporales para dimensiones de fecha. */
export const TIME_GRAINS = ['year', 'quarter', 'month', 'week', 'day', 'hour'] as const;
export const TimeGrainSchema = z.enum(TIME_GRAINS);
export type TimeGrain = z.infer<typeof TimeGrainSchema>;

/** Rol analitico de un campo. */
export const FIELD_ROLES = ['dimension', 'measure', 'attribute', 'identifier'] as const;
export const FieldRoleSchema = z.enum(FIELD_ROLES);
export type FieldRole = z.infer<typeof FieldRoleSchema>;
